<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  copy: {
    type: String,
    default: null,
  },
  // working around a stupid
  copyString: {
    type: String,
    default: null,
  },
  cta: {
    type: Object,
    default: null,
  },
  gtmClass: {
    type: String,
    default: null,
  },
})

const icon = computed(() =>
  props.cta?.download ? 'download' : 'chevron-right'
)
</script>

<template>
  <div class="cta-banner">
    <div class="cta-banner__left">
      <p class="text__headline-6">{{ title }}</p>
      <p v-if="copy || copyString">{{ copy || copyString }}</p>
    </div>

    <div class="cta-banner__right">
      <CallToAction
        v-bind="cta"
        :gtm-class="gtmClass"
        outline
        :icon="icon"
        hide-size
        animate
      />
    </div>
  </div>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.cta-banner {
  position: relative;
  background: radial-gradient(
      100% 100% at 50% 100%,
      rgba(#c80afb, 0.2) 0%,
      rgba(200, 10, 251, 0.126) 31.77%,
      rgba(200, 10, 251, 0.072) 60.94%,
      rgba(200, 10, 251, 0) 99.94%
    ),
    radial-gradient(
      203.33% 570.08% at 4.97% 195.19%,
      rgba(21, 42, 244, 0.15) 1.1%,
      rgba(21, 42, 244, 0) 100%
    ),
    radial-gradient(
      276.21% 299.86% at 51.35% 214.23%,
      rgba(106, 24, 247, 0.15) 0%,
      rgba(106, 24, 247, 0) 100%
    ),
    radial-gradient(
      75.27% 490.7% at 8.16% -28.46%,
      rgba(200, 10, 251, 0.1) 0%,
      rgba(200, 10, 251, 0) 100%
    ),
    #080711;
  padding: 50px gs(1, 0, 'xxxs');
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  max-width: 1102px;
  margin: 0 auto;

  @include mq($from: 'l') using($from) {
    background: radial-gradient(
        370px 553px at 85% 50%,
        rgba(#c80afb, 0.2) 0%,
        rgba(200, 10, 251, 0.126) 31.77%,
        rgba(200, 10, 251, 0.072) 60.94%,
        rgba(200, 10, 251, 0) 99.94%
      ),
      radial-gradient(
        203.33% 570.08% at 4.97% 195.19%,
        rgba(21, 42, 244, 0.15) 1.1%,
        rgba(21, 42, 244, 0) 100%
      ),
      radial-gradient(
        276.21% 299.86% at 51.35% 214.23%,
        rgba(106, 24, 247, 0.15) 0%,
        rgba(106, 24, 247, 0) 100%
      ),
      radial-gradient(
        75.27% 490.7% at 8.16% -28.46%,
        rgba(200, 10, 251, 0.1) 0%,
        rgba(200, 10, 251, 0) 100%
      ),
      #080711;
    padding: 0 gs(1, 1, $from);
    flex-direction: row;
    height: 255px;
  }

  &::after {
    @include mq($from: 'l') using($from) {
      position: absolute;
      left: -45.6%;
      right: -45.6%;
      top: 87.53%;
      bottom: -5.29%;
      width: 100%;
      height: 100%;
      content: '';
      background: radial-gradient(
        50% 50% at 50% 50%,
        #c80afb 0%,
        rgba(200, 10, 251, 0.63) 31.77%,
        rgba(200, 10, 251, 0.36) 60.94%,
        rgba(200, 10, 251, 0) 99.94%
      );
      opacity: 0.2;
      transform: rotate(90deg);
    }
  }

  &__left {
    text-align: center;
    max-width: 500px;

    @include mq($from: 'l') using($from) {
      text-align: left;
    }
  }

  p {
    @include mq($until: 'l') using($until) {
      padding-left: 30px;
      padding-right: 30px;
    }
  }

  p + p {
    margin-top: 16px;

    @include mq($from: 'l') using($from) {
      margin-top: 8px;
    }
  }

  &__right {
    padding-top: 80px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    flex-shrink: 0;

    @include mq($from: 'l') using($from) {
      padding-top: 0;
      height: 100%;
    }

    .button-pill {
      @include mq($until: 'l') using($until) {
        padding-left: 40px;
        padding-right: 40px;
      }
    }

    &::before,
    &::after {
      content: '';
      border: solid 1.5px #c80afb;
      border-radius: 50%;
      aspect-ratio: 1/1;
      width: 300%;
      height: auto;
      position: absolute;
      top: 265%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0.2;
      pointer-events: none;

      @include mq($from: 'l') using($from) {
        width: auto;
        height: 200%;
        top: 50%;
      }
    }

    &::after {
      width: 340%;
      opacity: 0.1;

      @include mq($from: 'l') using($from) {
        width: auto;
        height: 250%;
      }
    }

    // @include mq($from: 'l') using($from) {
    //   width: gs(6, 6, $from);
    // }
  }
}
</style>
